import {
  NAME,
  BUY_ADDRESS_DOMESTIC,
  BUY_ADDRESS_FOREIGN,
  REGION_CHINA,
  REGION_SINGAPORE,
  REGION_FRANKFURT,
  REGION_SEOUL,
  REGION_BOMBAY,
  REGION_SILICON,
} from '../constants';

// -----------------检测类型工具函数-----------------
/**
 * 检测input类型是否为Map
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->map / false->not a map
 */
export const isMap = function(input) {
  return getType(input) === NAME.MAP;
};

/**
 * 检测input类型是否为Set
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->set / false->not a set
 */
export const isSet = function(input) {
  return getType(input) === NAME.SET;
};

/**
 * 检测input类型是否为File
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->file / false->not a file
 */
export const isFile = function(input) {
  return getType(input) === NAME.FILE;
};

/**
 * 检测input类型是否为number
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->number / false->not a number
 */
export const isNumber = function(input) {
  return (
    input !== null &&
    ((typeof input === NAME.NUMBER && !isNaN(input - 0)) || (typeof input === NAME.OBJECT && input.constructor === Number))
  );
};

/**
 * 检测input类型是否为string
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->string / false->not a string
 */
export const isString = function(input) {
  return typeof input === NAME.STRING;
};

export const isObject = function(input) {
  // null is object, hence the extra check
  return input !== null && typeof input === NAME.OBJECT;
};

/**
 * 检测 input 类型是否为 boolean
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->boolean / false->not a boolean
 */
export const isBoolean = function(input) {
  return typeof input === NAME.BOOLEAN;
};

/**
 * Checks to see if a value is an object and only an object
 * plain object: 没有标准定义，一般认为通过 {} 或者 new Object() 或者 Object.create(null) 方式创建的对象是纯粹对象
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->an object and only an object
 */
export const isPlainObject = function(input) {
  // 注意不能使用以下方式判断，因为IE9/IE10下，对象的__proto__是undefined
  // return isObject(input) && input.__proto__ === Object.prototype;

  if (typeof input !== NAME.OBJECT || input === null) {
    return false;
  }

  const proto = Object.getPrototypeOf(input);
  if (proto === null) {
    // edge case Object.create(null)
    return true;
  }

  let baseProto = proto;
  while (Object.getPrototypeOf(baseProto) !== null) {
    baseProto = Object.getPrototypeOf(baseProto);
  }
  // 2. 原型链第一个和最后一个比较
  return proto === baseProto;
};

/**
 * 检测input类型是否为数组
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->array / false->not an array
 */
export const isArray = function(input) {
  if (typeof Array.isArray === NAME.FUNCTION) {
    return Array.isArray(input);
  }
  return getType(input) === NAME.ARRAY;
};

/**
 * Checks to see if a value is undefined
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->input value is undefined
 */
export const isUndefined = function(input) {
  return typeof input === NAME.UNDEFINED;
};

const isNativeClassRegex = /^class\s/;
/**
 * Is ES6+ class
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->input is ES6+ class
 */
export const isNativeClass = function(input) {
  return typeof input === NAME.FUNCTION && isNativeClassRegex.test(input.toString());
};

/**
 * 检测input类型是否为数组或者object
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->input is an array or an object
 */
export const isArrayOrObject = function(input) {
  return isArray(input) || isObject(input);
};

/**
 * 检测input类型是否为function
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->input is a function
 */
export const isFunction = function(input) {
  return typeof input === NAME.FUNCTION;
};

/**
 * 检测input是否为Error的实例
 * @param {*} input 任意类型的输入
 * @returns {Boolean} true->input is an instance of Error
 */
export const isInstanceOfError = function(input) {
  return input instanceof Error;
};

/**
 * Get the object type string
 * @param {*} input 任意类型的输入
 * @returns {String} the object type string
 */
export const getType = function(input) {
  return Object.prototype.toString
    .call(input)
    .match(/^\[object (.*)\]$/)[1]
    .toLowerCase();
};

// -----------------获取时间工具函数，计算耗时用-----------------

let baseTime = 0;
if (!Date.now) {
  Date.now = function now() {
    return new Date().getTime();
  };
}

export const TimeUtil = {
  now() {
    if (baseTime === 0) {
      baseTime = Date.now() - 1;
    }

    const diff = Date.now() - baseTime;
    if (diff > 0xffffffff) {
      baseTime += 0xffffffff;
      return Date.now() - baseTime;
    }
    return diff;
  },

  utc() {
    return Math.round(Date.now() / 1000);
  },
};

// -----------------其它-----------------
/**
 * 序列化Error实例，只序列化Error实例的message和code属性（如果有的话）
 * @param {Error} error Error实例
 * @returns {String} 序列化后的内容
 */
export const stringifyError = function(error) {
  return JSON.stringify(error, ['message', 'code']);
};

/**
 * 把map的内容转成字符串，
 * @param {Map} input 字典
 * @returns {String} 字典的内容
 */
export const map2String = function(input) {
  if (!isMap(input)) {
    return 'not a map!!!';
  }

  let result = '';
  for (const [k, v] of input.entries()) {
    if (!isMap(v)) {
      result += `[k=${k} v=${v}] `;
    } else {
      return `[k=${k} -> ${map2String(v)}`;
    }
  }
  return result;
};

const CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
const CHARS_LENGTH = CHARS.length;
/**
 * 获取[0,9],[a-z],[A,Z]拼成的随机字符串，长度32
 * @returns {String} 长度为32的随机字符串
 */
export const randomString = function() {
  let result = '';
  for (let i = 32; i > 0; --i) {
    result += CHARS[Math.floor(Math.random() * CHARS_LENGTH)];
  }
  return result;
};

// 判断传入的枚举值是否有效
export const isValidType = function(obj, type) {
  for (const k in obj) {
    if (obj[k] === type) {
      return true;
    }
  }
  return false;
};

// -----------------类lodash.mapKeys函数-----------------
export function mapKeys(obj, iteratee) {
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    newObj[iteratee(obj[key], key)] = obj[key];
  });
  return newObj;
}

// -----------------类lodash.mapValues函数-----------------
export function mapValues(obj, iteratee) {
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    newObj[key] = iteratee(obj[key], key);
  });
  return newObj;
}

// 随机生成 room id
export function generateRandomRoomID() {
  return Math.round(Math.random() * 100000000);
}
/**
 * 根据传入的 intRoomId, strRoomId 返回使用的房间号
 * 1. intRoomId 是大于 0 的数字, 返回 intRoomId
 * 2. intRoomId 为数字 0 时, 如果 strRoomId 为非空字符串, 返回字符串房间号; 否则返回 undefined
 * @param {Number} intRoomId 数字房间号
 * @param {String} strRoomId 字符串房间号
 * @returns {Number | String} 房间号
 */
export function analyzeRoomId(intRoomId, strRoomId) {
  if (isNumber(intRoomId) && intRoomId > 0) return intRoomId;
  if (intRoomId === 0 && strRoomId) return strRoomId;
  return generateRandomRoomID();
}

function formatInt(int) {
  return int < 10 ? `0${int}` : int;
}

export function formatDuration(duration) {
  if (duration < 60) {
    return `00:00:${formatInt(duration)}`;
  }
  if (duration < 60 * 60) {
    const min = parseInt(duration / 60);
    const sec = duration - min * 60;
    return `00:${formatInt(min)}:${formatInt(sec)}`;
  }
  const hour = parseInt(duration / (60 * 60));
  const remainder = duration - hour * (60 * 60);
  const min = parseInt(remainder / 60);
  const sec = remainder - min * 60;
  return `${formatInt(hour)}:${formatInt(min)}:${formatInt(sec)}`;
}

export function compareVersion(v1, v2) {
  v1 = v1.split('.');
  v2 = v2.split('.');
  const len = Math.max(v1.length, v2.length);

  while (v1.length < len) {
    v1.push('0');
  }
  while (v2.length < len) {
    v2.push('0');
  }

  for (let i = 0; i < len; i++) {
    const num1 = parseInt(v1[i]);
    const num2 = parseInt(v2[i]);

    if (num1 > num2) {
      return 1;
    }
    if (num1 < num2) {
      return -1;
    }
  }

  return 0;
}

/**
 * 返回一个 ISO（ISO 8601 Extended Format）格式的字符串，如"2019-11-15T18:45:06.000+0800"，用于记录sso上报时的时间
 * @returns {String}
 */
export const date2ISOString = function() {
  const date = new Date();
  // YYYY-MM-DDTHH:mm:ss.sssZ。时区总是UTC（协调世界时），加一个后缀“Z”标识。
  const tempISOString = date.toISOString();
  // 返回协调世界时（UTC）相对于当前时区的时间差值，单位为分钟。
  // 如果本地时区晚于协调世界时，则该差值为正值，如果早于协调世界时则为负值
  const timezoneOffset = date.getTimezoneOffset();
  const currentTimeZoneInHour = timezoneOffset / 60;
  let replaceString = '';
  if (currentTimeZoneInHour < 0) {
    // 东区
    if (currentTimeZoneInHour > -10) {
      replaceString = `+0${Math.abs(currentTimeZoneInHour * 100)}`;
    } else {
      replaceString = `+${Math.abs(currentTimeZoneInHour * 100)}`;
    }
  } else {
    // 西区
    if (currentTimeZoneInHour >= 10) {
      replaceString = `-${currentTimeZoneInHour * 100}`;
    } else {
      replaceString = `-0${currentTimeZoneInHour * 100}`;
    }
  }
  // 不需要 encodeURIComponent 把 + 转成 %2B，kibana能识别
  return tempISOString.replace('Z', replaceString);
};

// -----------------检测终端类型函数-----------------
/**
  * 判断是否是国内站创建的SDKAppID
  * 前缀是14、17开头的，中国站购买;
  * 如果是非这两个的前缀开头的，去国际站购买；
*/
export function isDomesticSDKAppID(sdkAppId) {
  const sdkAppIdStr = String(sdkAppId);
  return sdkAppIdStr.startsWith('14') || sdkAppIdStr.startsWith('16') || sdkAppIdStr.startsWith('17');
}
export function getPurchaseAddress(sdkAppId) {
  const address = isDomesticSDKAppID(sdkAppId) ? BUY_ADDRESS_DOMESTIC : BUY_ADDRESS_FOREIGN;
  return `${address}&position=${sdkAppId}&regionId=${getRegionId(sdkAppId)}`;
}

/**
  * 根据 SDKAppID 号段设置服务器信息
  * 14xxxxxxxx，已占用，国内站
  * [2kw ~ 3kw) || [172kw ~ 173kw)，新加坡站
  * [3kw ~ 4kw) || [173kw ~ 174kw)，韩国站
  * [4kw ~ 5kw) || [174kw ~ 175kw)，德国站
  * [5kw ~ 6kw) || [175kw ~ 176kw)，印度站
  * [6kw ~ 7kw) || [176kw ~ 177kw)，日本站 : 暂无
  * [7kw ~ 8kw) || [177kw ~ 178kw)，美国站
  * TAPD: https://tapd.woa.com/IM/prong/stories/view/1020398462886556185
*/
export function getRegionId(sdkAppId) {
  if ((sdkAppId >= 20000000 && sdkAppId < 30000000) || (sdkAppId >= 1720000000 && sdkAppId < 1730000000)) {
    return REGION_SINGAPORE;
  }
  if ((sdkAppId >= 30000000 && sdkAppId < 40000000) || (sdkAppId >= 1730000000 && sdkAppId < 1740000000)) {
    return REGION_SEOUL;
  }
  if ((sdkAppId >= 40000000 && sdkAppId < 50000000) || (sdkAppId >= 1740000000 && sdkAppId < 1750000000)) {
    return REGION_FRANKFURT;
  }
  if ((sdkAppId >= 50000000 && sdkAppId < 60000000) || (sdkAppId >= 1750000000 && sdkAppId < 1760000000)) {
    return REGION_BOMBAY;
  }
  if ((sdkAppId >= 70000000 && sdkAppId < 80000000) || (sdkAppId >= 1770000000 && sdkAppId < 1780000000)) {
    return REGION_SILICON;
  }
  return REGION_CHINA;
}

// 获取value的类型
export function getValueType(value) {
  return Reflect.apply(Object.prototype.toString, value, [])
    .replace(/^\[object\s(\w+)\]$/, '$1')
    .toLowerCase();
}

export function safelyParse(data) {
  if (typeof data !== 'string') {
    return data;
  }
  let result;
  try {
    const tempData = JSON.parse(data);
    if (typeof tempData === 'object' && tempData) {
      result = tempData;
    } else {
      result = data;
    }
  } catch (error) {
    result = data;
  }
  return result;
}

export function generateRandomString(length) {
  length = length || 32;
  const seed = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += seed.charAt(Math.floor(Math.random() * (seed.length)));
  }

  return result;
}

// 兼容小程序插件环境
function getAuthSetting() {
  return new Promise(async (resolve, reject) => {
    wx.getSetting({
      success: (res) => {
        let authSetting = null;
        if (!wx.$TUICallKitPlugin) {
          authSetting = res.authSetting;
        } else {
          authSetting = res.miniprogramAuthSetting;
        }
        resolve(authSetting);
      },
      fail: (error) => {
        reject(error);
      },
    });
  });
}
/**
 * 获取设备权限，查看当前是否有麦克风以及摄像头权限
 * @param {Object} params - 参数对象
 * @param {Boolean} params.microphone 麦克风权限
 * @param {Boolean} params.camera     摄像头权限
 */
export async function deviceCheck(params) {
  if (!params) return false;
  const { microphone = false, camera = false } = params;
  try {
    const authSetting = await getAuthSetting() || false;
    const isMicrophone = authSetting[DEVICE_SCOPE.RECORD];
    const isCamera = authSetting[DEVICE_SCOPE.CAMERA];
    // 同时检查麦克风和摄像头权限
    if (microphone && camera) {
      return isMicrophone && isCamera;
    }
    // 只检查麦克风权限
    if (microphone) {
      return isMicrophone;
    }
    // 只检查摄像头权限
    if (camera) {
      return isCamera;
    }
    return false;
  } catch (error) {
    return false;
  }
}

// 获取微信 App 权限，是否有麦克风以及摄像头权限
export function appDeviceCheck(params) {
  if (!params) return false;
  const { microphone = false, camera = false } = params;
  const appAuthorizeSetting = wx.getAppAuthorizeSetting();
  const microphoneAuthorized = appAuthorizeSetting[APP_DEVICE_SCOPE.MICROPHONE_AUTHORIZED] === WX_APP_PERMISSION_CHECK_RESULT.AUTHORIZED;
  const cameraAuthorized = appAuthorizeSetting[APP_DEVICE_SCOPE.CAMERA_AUTHORIZED] === WX_APP_PERMISSION_CHECK_RESULT.AUTHORIZED;
  // 同时检查麦克风和摄像头权限
  if (microphone && camera) {
    return microphoneAuthorized && cameraAuthorized;
  }
  // 只检查麦克风权限
  if (microphone) {
    return microphoneAuthorized;
  }
  // 只检查摄像头权限
  if (camera) {
    return cameraAuthorized;
  }
  return false;
}

// callkit 的 offlinePushInfo 转成 c++ 解析的字段
export function convertOfflinePushInfoToCIdentifier (offlinePushInfo = {}) {
  let obj = {
    title: offlinePushInfo.title,
    desc: offlinePushInfo.description || '',
    extension: offlinePushInfo.extension || '',
    is_disable_push: offlinePushInfo.isDisablePush || false,
    ios_push_type: offlinePushInfo.iOSPushType || 0, // 默认 “普通推送”, 1-"voip 推送"
    android_push_type: offlinePushInfo.androidPushType || 0, // 默认 notification, 1-data
    ignore_ios_badge: !!offlinePushInfo.ignoreIOSBadge,
    android_vivo_classification: offlinePushInfo.androidVIVOClassification, // 已废弃
  };
  if (offlinePushInfo.iOSSound) {
    obj.ios_sound = offlinePushInfo.ios_sound;
  }
  if (offlinePushInfo.androidSound) {
    obj.android_sound = offlinePushInfo.androidSound;
  }
  if (offlinePushInfo.androidOPPOChannelID) {
    obj.android_oppo_channel_id = offlinePushInfo.androidOPPOChannelID;
  }
  if (offlinePushInfo.androidFCMChannelID) {
    obj.android_fcm_channel_id = offlinePushInfo.androidFCMChannelID;
  }
  if (offlinePushInfo.androidXiaoMiChannelID) {
    obj.android_xiaomi_channel_id = offlinePushInfo.androidXiaoMiChannelID;
  }
  if (offlinePushInfo.androidHuaWeiCategory) {
    obj.android_huawei_category = offlinePushInfo.androidHuaWeiCategory;
  }
  return obj;
}
